import { faArrowRight, faChevronLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton, Stack, Typography } from "@mui/material";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";

import { useFeatureToggleContext } from "contexts/FeatureToggleContext";
import { useMessagesSearchParams } from "routes/messages/hooks/useMessagesSearchParams";
import { PatientHeaderInfoAddNoteButton } from "routes/patients/PatientProfile/components/PatientHeader/PatientHeaderInfo/PatientHeaderInfoAddNoteButton";
import { Avatar } from "shared/atoms/Avatar";
import { BaseButton } from "shared/atoms/BaseButton";
import { SkeletonLayout } from "shared/atoms/SkeletonLayout";
import { DARK_BLUE } from "theme/colors";
import { Feature } from "utils/features/types";

import { CustomNavLink } from "../styles";

interface Props {
  isLoading: boolean;
  patientID: string | undefined;
  patientName: string | undefined;
  patientAvatarSrc?: string | undefined;
}

export const ConversationHeader: React.FC<Props> = ({ isLoading, patientID, patientName, patientAvatarSrc }) => {
  const { t } = useTranslation();
  const { isDetailsOpen, togglePanel } = useMessagesSearchParams();
  const { hasFeature } = useFeatureToggleContext();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleDetailsClick = () => {
    togglePanel("details");
  };

  return (
    <>
      <MobileHeader>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
          <IconButton size="small" aria-label={t("buttons.go_back")} onClick={() => navigate("../")}>
            <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 14 }} />
          </IconButton>
        </Stack>

        <Stack justifyContent="center" flex={1}>
          {isLoading && !patientName ? (
            <SkeletonLayout layout={{ type: "skeleton", variant: "circular", width: 36, height: 36 }} />
          ) : (
            <AvatarLink to={`/patients/${patientID}`} aria-label={patientName}>
              <Avatar
                size={36}
                fontSize={13}
                id={patientID ? Number(patientID) : undefined}
                name={patientName ?? ""}
                src={patientAvatarSrc}
              />
              <MobilePatientName>{patientName}</MobilePatientName>
            </AvatarLink>
          )}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          <BaseButton
            variant="action"
            onClick={handleDetailsClick}
            text={t("messages.patient_details")}
            icon="user"
            iconPlacement="end"
            className={clsx({ active: isDetailsOpen })}
          />
        </Stack>
      </MobileHeader>

      <Stack
        data-testid="conversation-header"
        direction="row"
        spacing={1}
        padding={2}
        sx={{
          display: "none",
          [`@media (min-width: ${theme.breakpoint}px)`]: { display: "flex" },
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          boxSizing: "border-box",
          minHeight: "74px",
          borderBottom: `1px solid ${DARK_BLUE[10]}`,
        }}
      >
        {isLoading && !patientName && (
          <SkeletonLayout
            layout={{
              type: "row",
              justifyContent: "space-between",
              alignItems: "center",
              children: [
                { type: "skeleton", variant: "text", typographyVariant: "h3", width: "30%" },
                { type: "skeleton", variant: "rectangular", width: 100, height: 40 },
              ],
            }}
          />
        )}
        {patientName && (
          <>
            <PatientLink to={`/patients/${patientID}`}>
              <Typography variant="body_bold">{patientName}</Typography>
              <FontAwesomeIcon icon={faArrowRight} />
            </PatientLink>

            <Stack direction="row" spacing={1}>
              <BaseButton
                variant="action"
                onClick={handleDetailsClick}
                text={t("messages.patient_details")}
                icon="user"
                iconPlacement="end"
                className={clsx({ active: isDetailsOpen })}
              />
              {hasFeature(Feature.WRITE_PATIENT_NOTE) && <PatientHeaderInfoAddNoteButton />}
            </Stack>
          </>
        )}
      </Stack>
    </>
  );
};

// The mobile header and the desktop header flip at the same theme breakpoint where the
// conversation list switches to a side-by-side layout (theme.aboveBreakpoint = min-width 767px),
// so there is never a gap or overlap between the two.
const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid ${DARK_BLUE[10]};
  background: ${({ theme }) => theme.colors.white};
  position: sticky;
  top: 0;
  z-index: 10;

  ${({ theme }) => theme.aboveBreakpoint} {
    display: none;
  }
`;

const AvatarLink = styled(CustomNavLink)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  text-decoration: none;
`;

const MobilePatientName = styled.span`
  font-size: 11px;
  color: ${DARK_BLUE[60]};
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PatientLink = styled(CustomNavLink)`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  ${({ theme }) => theme.aboveBreakpoint} {
    order: initial;
  }
`;
